import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlayIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  AcademicCapIcon,
  LightBulbIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const ApplicationTutorials = ({ userRole }) => {
  const [expandedTutorial, setExpandedTutorial] = useState(null);
  const [completedTutorials, setCompletedTutorials] = useState([]);
  const [bookmarkedTutorials, setBookmarkedTutorials] = useState([]);

  const tutorials = [
    {
      id: 'business-plan',
      title: 'Writing a Winning Business Plan',
      description:
        'Learn how to create a compelling business plan that stands out to grant reviewers',
      duration: '45 min',
      difficulty: 'Intermediate',
      instructor: 'Mr. Wetshootsile G Dintwe',
      category: 'Planning',
      videoUrl: 'https://example.com/business-plan-tutorial',
      thumbnail: '/api/placeholder/400/225',
      steps: [
        {
          step: 1,
          title: 'Executive Summary',
          description: 'Write a compelling one-page summary that captures your vision',
          duration: '10 min',
          videoUrl: 'https://example.com/executive-summary',
          template: 'executive-summary-template.docx',
        },
        {
          step: 2,
          title: 'Market Analysis',
          description: 'Conduct thorough market research and competitive analysis',
          duration: '15 min',
          videoUrl: 'https://example.com/market-analysis',
          template: 'market-analysis-template.xlsx',
        },
        {
          step: 3,
          title: 'Financial Projections',
          description: 'Create realistic financial forecasts and funding requirements',
          duration: '20 min',
          videoUrl: 'https://example.com/financial-projections',
          template: 'financial-projections-template.xlsx',
        },
      ],
      resources: [
        'Business Plan Template',
        'Market Research Guide',
        'Financial Projection Calculator',
      ],
    },
    {
      id: 'grant-writing',
      title: 'Grant Writing Fundamentals',
      description: 'Master the art of persuasive grant writing and proposal development',
      duration: '60 min',
      difficulty: 'Advanced',
      instructor: 'Boipelo Makua',
      category: 'Writing',
      videoUrl: 'https://example.com/grant-writing-tutorial',
      thumbnail: '/api/placeholder/400/225',
      steps: [
        {
          step: 1,
          title: 'Understanding Grant Requirements',
          description: 'Analyze grant guidelines and align your proposal',
          duration: '15 min',
          videoUrl: 'https://example.com/grant-requirements',
          template: 'grant-requirements-checklist.pdf',
        },
        {
          step: 2,
          title: 'Crafting the Problem Statement',
          description: 'Define the problem your project will solve',
          duration: '20 min',
          videoUrl: 'https://example.com/problem-statement',
          template: 'problem-statement-template.docx',
        },
        {
          step: 3,
          title: 'Developing the Solution',
          description: 'Present your innovative solution and methodology',
          duration: '25 min',
          videoUrl: 'https://example.com/solution-development',
          template: 'solution-framework-template.docx',
        },
      ],
      resources: ['Grant Writing Style Guide', 'Proposal Template', 'Common Mistakes Checklist'],
    },
    {
      id: 'financial-projections',
      title: 'Financial Projections & Budgeting',
      description: 'Create compelling financial projections that demonstrate viability',
      duration: '75 min',
      difficulty: 'Advanced',
      instructor: 'Christopher Batsalelwang',
      category: 'Finance',
      videoUrl: 'https://example.com/financial-projections-tutorial',
      thumbnail: '/api/placeholder/400/225',
      steps: [
        {
          step: 1,
          title: 'Revenue Forecasting',
          description: 'Build realistic revenue projections based on market data',
          duration: '25 min',
          videoUrl: 'https://example.com/revenue-forecasting',
          template: 'revenue-forecast-template.xlsx',
        },
        {
          step: 2,
          title: 'Cost Structure Analysis',
          description: 'Identify and categorize all project costs',
          duration: '30 min',
          videoUrl: 'https://example.com/cost-analysis',
          template: 'cost-structure-template.xlsx',
        },
        {
          step: 3,
          title: 'Cash Flow Management',
          description: 'Develop cash flow projections and funding timeline',
          duration: '20 min',
          videoUrl: 'https://example.com/cash-flow',
          template: 'cash-flow-template.xlsx',
        },
      ],
      resources: ['Financial Modeling Guide', 'Budget Template', 'Cash Flow Calculator'],
    },
    {
      id: 'pitch-presentation',
      title: 'Pitch Presentation Skills',
      description: 'Deliver compelling presentations to grant review panels',
      duration: '50 min',
      difficulty: 'Intermediate',
      instructor: 'Communication Expert David Kim',
      category: 'Presentation',
      videoUrl: 'https://example.com/pitch-presentation-tutorial',
      thumbnail: '/api/placeholder/400/225',
      steps: [
        {
          step: 1,
          title: 'Storytelling Structure',
          description: 'Organize your presentation with a compelling narrative',
          duration: '20 min',
          videoUrl: 'https://example.com/storytelling',
          template: 'presentation-outline-template.pptx',
        },
        {
          step: 2,
          title: 'Visual Design',
          description: 'Create impactful slides that support your message',
          duration: '15 min',
          videoUrl: 'https://example.com/visual-design',
          template: 'slide-template.pptx',
        },
        {
          step: 3,
          title: 'Delivery Techniques',
          description: 'Master presentation delivery and Q&A handling',
          duration: '15 min',
          videoUrl: 'https://example.com/delivery-techniques',
          template: 'qa-preparation-guide.pdf',
        },
      ],
      resources: ['Presentation Template', 'Public Speaking Guide', 'Q&A Preparation Checklist'],
    },
  ];

  const toggleTutorial = tutorialId => {
    setExpandedTutorial(expandedTutorial === tutorialId ? null : tutorialId);
  };

  const markTutorialComplete = tutorialId => {
    if (!completedTutorials.includes(tutorialId)) {
      setCompletedTutorials([...completedTutorials, tutorialId]);
      toast.success('Tutorial completed! Great job!');
    }
  };

  const toggleBookmark = tutorialId => {
    if (bookmarkedTutorials.includes(tutorialId)) {
      setBookmarkedTutorials(bookmarkedTutorials.filter(id => id !== tutorialId));
      toast.info('Removed from bookmarks');
    } else {
      setBookmarkedTutorials([...bookmarkedTutorials, tutorialId]);
      toast.success('Added to bookmarks');
    }
  };

  const downloadTemplate = templateName => {
    toast.success(`Downloading ${templateName}...`);
  };

  const getDifficultyColor = difficulty => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Intermediate':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Advanced':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-black/70 border-black/20';
    }
  };

  const getCategoryIcon = category => {
    switch (category) {
      case 'Planning':
        return AcademicCapIcon;
      case 'Writing':
        return DocumentTextIcon;
      case 'Finance':
        return LightBulbIcon;
      case 'Presentation':
        return UserIcon;
      default:
        return DocumentTextIcon;
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header Card */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600 sm:h-14 sm:w-14">
              <PlayIcon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Application Tutorials
              </h1>
              <p className="mt-1 text-sm text-gray-600 sm:text-base">
                Step-by-step guides to master grant applications
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-6 py-4 sm:px-8">
            <div className="text-xs font-medium text-gray-600 sm:text-sm">Completed</div>
            <div className="text-3xl font-bold text-gray-900 sm:text-4xl">
              {completedTutorials.length}/{tutorials.length}
            </div>
          </div>
        </div>
      </motion.div>

      <div className="space-y-6">
        {tutorials.map((tutorial, index) => {
          const CategoryIcon = getCategoryIcon(tutorial.category);
          const isExpanded = expandedTutorial === tutorial.id;
          const isCompleted = completedTutorials.includes(tutorial.id);
          const isBookmarked = bookmarkedTutorials.includes(tutorial.id);

          return (
            <motion.div
              key={tutorial.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="border-b border-gray-200 bg-gray-50 p-6 transition-colors hover:bg-gray-100 sm:p-8">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1">
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
                        <CategoryIcon className="h-5 w-5 text-white" />
                      </div>
                      <span className="rounded-full border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700">
                        {tutorial.category}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${getDifficultyColor(tutorial.difficulty)}`}
                      >
                        {tutorial.difficulty}
                      </span>
                      {isCompleted && (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-600">
                          <CheckCircleIcon className="h-5 w-5 text-white" />
                        </div>
                      )}
                    </div>
                    <h2 className="mb-3 text-2xl font-bold text-gray-900 sm:text-3xl">
                      {tutorial.title}
                    </h2>
                    <p className="mb-6 text-gray-600 sm:text-lg">{tutorial.description}</p>

                    <div className="mb-6 flex flex-wrap items-center gap-6 text-sm text-gray-600">
                      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                        <ClockIcon className="h-5 w-5 text-gray-600" />
                        <span className="font-medium">{tutorial.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                        <UserIcon className="h-5 w-5 text-gray-600" />
                        <span className="font-medium">{tutorial.instructor}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => toggleTutorial(tutorial.id)}
                        className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
                      >
                        {isExpanded ? 'Hide Details' : 'View Tutorial'}
                        {isExpanded ? (
                          <ChevronDownIcon className="h-5 w-5" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5" />
                        )}
                      </motion.button>

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => toggleBookmark(tutorial.id)}
                        className={`flex items-center gap-2 rounded-lg border px-6 py-3 font-semibold transition-colors ${
                          isBookmarked
                            ? 'border-blue-300 bg-blue-50 text-blue-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <BookmarkIcon className={`h-5 w-5 ${isBookmarked ? 'fill-current' : ''}`} />
                        {isBookmarked ? 'Bookmarked' : 'Bookmark'}
                      </motion.button>

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center gap-2 rounded-lg bg-gray-900 px-6 py-3 font-semibold text-white transition-colors hover:bg-gray-800"
                      >
                        <PlayIcon className="h-5 w-5" />
                        Watch Intro
                      </motion.button>
                    </div>
                  </div>

                  <div className="flex-shrink-0 lg:ml-8">
                    <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-100 lg:h-32 lg:w-40">
                      <div className="flex h-32 w-full items-center justify-center lg:h-40 lg:w-40">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm">
                          <PlayIcon className="h-8 w-8 text-gray-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-white"
                  >
                    <div className="p-6 sm:p-8">
                      <div className="mb-8">
                        <h3 className="mb-6 text-xl font-bold text-gray-900">Tutorial Steps</h3>
                        <div className="space-y-4">
                          {tutorial.steps.map((step, stepIndex) => (
                            <motion.div
                              key={step.step}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: stepIndex * 0.1 }}
                              className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:flex-row sm:gap-6"
                            >
                              <div className="flex-shrink-0">
                                <div className="relative">
                                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600 sm:h-14 sm:w-14">
                                    <span className="text-base font-bold text-white sm:text-lg">
                                      {step.step}
                                    </span>
                                  </div>
                                  {stepIndex < tutorial.steps.length - 1 && (
                                    <div className="absolute left-1/2 top-full mt-2 h-8 w-0.5 -translate-x-1/2 bg-gray-300 sm:h-12"></div>
                                  )}
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                                  <h4 className="text-lg font-bold text-gray-900 sm:text-xl">
                                    {step.title}
                                  </h4>
                                  <span className="rounded-full border border-gray-300 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700">
                                    {step.duration}
                                  </span>
                                </div>
                                <p className="mb-4 text-gray-600">{step.description}</p>
                                <div className="flex flex-wrap items-center gap-3">
                                  <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                                  >
                                    <PlayIcon className="h-4 w-4" />
                                    Watch
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => downloadTemplate(step.template)}
                                    className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                                  >
                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                    Download Template
                                  </motion.button>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>

                      <div className="mb-8">
                        <h3 className="mb-6 text-xl font-bold text-gray-900">
                          Additional Resources
                        </h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {tutorial.resources.map((resource, idx) => (
                            <motion.button
                              key={idx}
                              whileHover={{ scale: 1.01 }}
                              whileTap={{ scale: 0.99 }}
                              onClick={() => downloadTemplate(resource)}
                              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-gray-300 hover:shadow-md"
                            >
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                                <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                              </div>
                              <span className="flex-1 text-left text-sm font-medium text-gray-700">
                                {resource}
                              </span>
                              <ArrowDownTrayIcon className="h-5 w-5 text-gray-400" />
                            </motion.button>
                          ))}
                        </div>
                      </div>

                      <div className="text-center">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => markTutorialComplete(tutorial.id)}
                          className={`rounded-lg px-8 py-3 font-semibold transition-colors ${
                            isCompleted
                              ? 'cursor-default bg-gray-100 text-gray-600'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {isCompleted ? (
                            <span className="flex items-center justify-center gap-2">
                              <CheckCircleIcon className="h-5 w-5" />
                              Completed
                            </span>
                          ) : (
                            'Mark as Complete'
                          )}
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {completedTutorials.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm sm:p-10"
        >
          <div className="mb-8 flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-600 sm:h-14 sm:w-14">
              <CheckCircleIcon className="h-7 w-7 text-white sm:h-8 sm:w-8" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">Your Progress</h3>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center"
            >
              <div className="text-3xl font-bold text-gray-900 sm:text-4xl">
                {completedTutorials.length}
              </div>
              <div className="mt-2 text-sm font-medium text-gray-600 sm:text-base">
                Tutorials Completed
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center"
            >
              <div className="text-3xl font-bold text-gray-900 sm:text-4xl">
                {bookmarkedTutorials.length}
              </div>
              <div className="mt-2 text-sm font-medium text-gray-600 sm:text-base">Bookmarked</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center"
            >
              <div className="text-3xl font-bold text-gray-900 sm:text-4xl">
                {Math.round((completedTutorials.length / tutorials.length) * 100)}%
              </div>
              <div className="mt-2 text-sm font-medium text-gray-600 sm:text-base">
                Overall Progress
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ApplicationTutorials;
